import { apiGet, apiPost, buildPaginationBody, API_ENDPOINTS } from "../utils/apiUtils";

/**
 * Custom Products Service
 * Handles custom product management
 */
const CustomProductsService = {
  /**
   * Get all custom products with pagination and filtering
   * @param {Object} options - Filter and pagination options
   * @param {number} options.page - Page number
   * @param {number} options.pageSize - Items per page
   * @param {string} options.searchtext - Search text
   * @param {string} options.category_id - Category ID filter (optional)
   * @param {boolean} options.is_active - Active status filter
   * @returns {Promise} API response
   */
  async getAllCustomProducts({ page = 1, pageSize = 10, searchtext = "", category_id = "", is_active = true } = {}) {
    const body = {
      pagination: {
        page,
        pageSize,
      },
      searchtext,
      is_active,
      ...(category_id && { category_id })
    };
    return apiPost(API_ENDPOINTS.CUSTOM_PRODUCTS.GET_ALL, body);
  },

  /**
   * Add new custom product
   * @param {Object} productData - Product creation data
   * @param {string} productData.image - Product image URL
   * @param {string} productData.title - Product title
   * @param {number} productData.price - Product price
   * @param {number} productData.discount_percentage - Discount percentage (0-100)
   * @param {string} productData.category_id - Category ID
   * @param {string} productData.product_url - Product URL (optional)
   * @returns {Promise} API response
   */
  async addCustomProduct(productData) {
    return apiPost(API_ENDPOINTS.CUSTOM_PRODUCTS.ADD, productData);
  },

  /**
   * Update custom product
   * @param {string} productId - Product ID
   * @param {Object} productData - Product update data
   * @returns {Promise} API response
   */
  async updateCustomProduct(productId, productData) {
    return apiPost(API_ENDPOINTS.CUSTOM_PRODUCTS.UPDATE, { 
      product_id: productId, 
      ...productData 
    });
  },

  /**
   * Delete custom product
   * @param {string} productId - Product ID
   * @returns {Promise} API response
   */
  async deleteCustomProduct(productId) {
    return apiPost(API_ENDPOINTS.CUSTOM_PRODUCTS.DELETE, { product_id: productId });
  },

  /**
   * Get custom product details
   * @param {string} productId - Product ID
   * @returns {Promise} API response
   */
  async getCustomProductDetails(productId) {
    return apiGet(API_ENDPOINTS.CUSTOM_PRODUCTS.GET_DETAILS(productId));
  },
};

export default CustomProductsService;
